import { useEffect, useState } from "react";
import {
  collection,
  CollectionReference,
  onSnapshot,
  QuerySnapshot,
  SnapshotOptions,
} from "firebase/firestore";
import deepequal from "fast-deep-equal";

import { useFirestore } from "../../contexts/firebase/FirebaseContext";

/**
 * Creates and caches a query reference from a collection
 * path, or an existing `firestore.CollectionReference` object.
 *
 * @param path A path to a collection or a reference to a collection.
 * @returns `ref`
 */
export const useCollectionReference = <T>(
  path?: string | CollectionReference<T> | null
): CollectionReference<T> | undefined => {
  const store = useFirestore();
  const [ref, setRef] = useState<CollectionReference<T>>();

  // Create reference
  useEffect(() => {
    if (!store) return;

    // Reset the ref if the path becomes empty
    if (!path) {
      setRef(undefined);
      return;
    }

    // Create new reference if path is different
    if (typeof path === "string") {
      if (path !== ref?.path)
        setRef(collection(store, path) as CollectionReference<T>);
    } else if (path.path !== ref?.path) {
      setRef(path);
    }
  }, [store, path, ref, setRef]);

  return ref;
};

/**
 * Retrieve and listen to documents in a collection or query.
 * A listener is created only when a valid path or reference
 * is given.
 *
 * Returns the following in an array:
 *  - The query snapshot when retrieved successfully. `undefined` otherwise.
 *  - A boolean representing the loading state of the query.
 *  - Error if an error occurred. `undefined` otherwise.
 *
 * @param path a path to a collection or a reference to a collection.
 * @returns `[snapshot, loading, error]`
 */
export const useCollection = <T>(
  path?: string | CollectionReference<T> | null
): [QuerySnapshot<T> | undefined, boolean, Error | undefined] => {
  const ref = useCollectionReference(path);

  const defaultLoading = Boolean(path);
  const [loading, setLoading] = useState(defaultLoading);
  const [error, setError] = useState<Error>();
  const [snapshot, setSnapshot] = useState<QuerySnapshot<T>>();

  // Listen for query updates
  useEffect(() => {
    // If there is no ref, reset data
    if (!ref) {
      setLoading(defaultLoading);
      setSnapshot(undefined);
      setError(undefined);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (cancelled) return;
        setSnapshot(snapshot);
        setLoading(false);
        setError(undefined);
      },
      (error) => {
        if (cancelled) return;
        setLoading(false);
        setError(error);
      }
    );

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [ref, defaultLoading, setLoading, setSnapshot, setError]);

  return [snapshot, loading, error];
};

/**
 * Retrieve and listen to a collection's data from Firestore.
 * A listener is created only when a valid path or reference
 * is given.
 *
 * Returns the following in an array:
 *  - The collection data. `undefined` otherwise.
 *  - A boolean representing the loading state of the collection snapshot.
 *  - Error if an error occurred. `undefined` otherwise.
 *
 * @param path A path or a reference to a collection.
 * @param options Snapshot options.
 * @returns `[data, loading, error]`
 */
export const useCollectionData = <T>(
  path?: string | CollectionReference<T> | null,
  options?: SnapshotOptions
): [T[] | undefined, boolean, Error | undefined] => {
  const [snapshot, loading, error] = useCollection(path);
  const [ops, setOps] = useState(options);
  const [data, setData] = useState<T[]>();

  // Update options if they change
  useEffect(() => {
    if (!deepequal(options, ops)) setOps(options);
  }, [options, ops, setOps]);

  // If the snapshot changes, extract the data
  useEffect(() => {
    setData(snapshot?.docs.map((snap) => snap.data(ops)));
  }, [setData, snapshot, ops]);

  return [data, loading, error];
};
